/**
 * utils/sorFailureManager.js
 * Utilitaire pour gérer les fichiers .sor des PV d'échec avec tentatives multiples
 */

export class SorFailureManager {
  /**
   * Nettoyer un nom de fichier des suffixes/préfixes de tentatives
   * @param {string} fileName - Nom du fichier original
   * @returns {Object} Nom nettoyé + numéro de tentative détecté
   */
  static cleanFileName(fileName) {
    const nameWithoutExt = fileName.replace(/\.sor$/i, "");

    // Patterns plus précis pour éviter les faux positifs
    const patterns = [
      // Suffixes: -2, *3, _4 (mais pas les longueurs d'onde comme 1310)
      { regex: /(.+)[-*_]([2-9])$/, type: "suffix" }, // Commence à 2 pour éviter 1310/1550
      // Préfixes: 2_, 3*, 4- (mais pas 1_)
      { regex: /^([2-9])[_*-](.+)$/, type: "prefix" },
    ];

    for (const pattern of patterns) {
      const match = nameWithoutExt.match(pattern.regex);
      if (match) {
        if (pattern.type === "suffix") {
          return {
            cleanName: match[1] + ".sor",
            originalName: fileName,
            attemptNumber: parseInt(match[2], 10),
            hasAttemptSuffix: true,
          };
        } else {
          // prefix
          return {
            cleanName: match[2] + ".sor",
            originalName: fileName,
            attemptNumber: parseInt(match[1], 10),
            hasAttemptSuffix: true,
          };
        }
      }
    }

    // Aucun pattern détecté = version originale
    return {
      cleanName: fileName,
      originalName: fileName,
      attemptNumber: 1,
      hasAttemptSuffix: false,
    };
  }

  /**
   * Grouper les fichiers par fibre avec gestion des tentatives
   * @param {Array} files - Liste des fichiers du répertoire
   * @returns {Object} Fichiers groupés avec tentatives
   */
  static groupFailureFiles(files) {
    const grouped = {};
    const attemptGroups = {}; // Grouper par nom nettoyé

    // Phase 1: Nettoyer et grouper par nom de base
    files.forEach((file) => {
      const cleaned = this.cleanFileName(file.name);
      const parsed = this.parseCleanFileName(cleaned.cleanName);

      if (!parsed) return;

      const { tiroir, module, port, longueurOnde } = parsed;
      const baseKey = `${tiroir}-${module}-${port}-${longueurOnde}`;

      if (!attemptGroups[baseKey]) {
        attemptGroups[baseKey] = {
          tiroir,
          module,
          port,
          longueurOnde,
          attempts: [],
        };
      }

      attemptGroups[baseKey].attempts.push({
        ...file,
        parsed,
        cleaned,
        baseKey,
      });
    });

    // Phase 2: Organiser en structure hiérarchique
    Object.values(attemptGroups).forEach((group) => {
      const { tiroir, module, port } = group;

      if (!grouped[tiroir]) grouped[tiroir] = {};
      if (!grouped[tiroir][module]) grouped[tiroir][module] = {};
      if (!grouped[tiroir][module][`Port_${port}`]) {
        grouped[tiroir][module][`Port_${port}`] = {
          port,
          measurementGroups: {},
          allAttempts: [],
          pboValues: new Set(),
        };
      }

      const fiber = grouped[tiroir][module][`Port_${port}`];

      // Grouper par longueur d'onde
      if (!fiber.measurementGroups[group.longueurOnde]) {
        fiber.measurementGroups[group.longueurOnde] = {
          wavelength: group.longueurOnde,
          attempts: [],
          attemptCount: 0,
        };
      }

      const measurement = fiber.measurementGroups[group.longueurOnde];
      measurement.attempts = group.attempts.sort((a, b) => a.cleaned.attemptNumber - b.cleaned.attemptNumber);
      measurement.attemptCount = measurement.attempts.length;

      // Ajouter à la liste globale
      fiber.allAttempts.push(...group.attempts);

      // Collecter les PBO
      group.attempts.forEach((attempt) => {
        if (attempt.parsed.pbo) {
          fiber.pboValues.add(attempt.parsed.pbo);
        }
      });
    });

    return grouped;
  }

  /**
   * Parser un nom de fichier nettoyé (réutilise la logique existante)
   */
  static parseCleanFileName(fileName) {
    try {
      // Suppression de l'extension .sor
      const nameWithoutExt = fileName.replace(/\.sor$/i, "");

      // Pattern pour capturer : (pbo)_(B2-T?-M?-???)_OE_(longueurOnde)
      const pattern = /^(.+)_(B2-T(\w)-M(\w)-(\d{3}))_OE_(\d+)$/;
      const match = nameWithoutExt.match(pattern);

      if (!match) {
        return null;
      }

      const [, pbo, , tiroir, moduleChar, port, longueurOnde] = match;

      return {
        fileName: fileName,
        tiroir: `T${tiroir}`,
        module: `MODULE_${moduleChar}`,
        port: parseInt(port, 10),
        longueurOnde: longueurOnde,
        pbo: pbo,
      };
    } catch (error) {
      return null;
    }
  }

  /**
   * Valider les tentatives d'une fibre
   * @param {Object} fiber - Données de la fibre
   * @returns {Object} Résultat de validation
   */

  static validateFiberAttempts(fiber) {
    const measurements = fiber.measurementGroups;
    const wavelengths = Object.keys(measurements);

    let totalAttempts = 0;
    const failedMeasurements = [];
    const successfulMeasurements = [];

    wavelengths.forEach((wavelength) => {
      const measurement = measurements[wavelength];
      totalAttempts += measurement.attemptCount;

      if (measurement.attemptCount > 1) {
        failedMeasurements.push(`${wavelength}nm: ${measurement.attemptCount}`);
      } else {
        successfulMeasurements.push(`${wavelength}nm`);
      }
    });

    const hasFailures = failedMeasurements.length > 0;
    const hasExpectedWavelengths = wavelengths.includes("1310") && wavelengths.includes("1550");

    let status = "success";
    let message = "";

    if (hasFailures) {
      status = "failure";
      message = `${failedMeasurements.join(" | ")}`;
    } else if (!hasExpectedWavelengths) {
      status = "incomplete";
      const missing = [];
      if (!wavelengths.includes("1310")) missing.push("1310nm");
      if (!wavelengths.includes("1550")) missing.push("1550nm");
      message = `Manque: ${missing.join(", ")}`;
    } else {
      status = "success";
      message = "1310nm + 1550nm";
    }

    return {
      isValid: status === "success",
      status,
      message,
      totalAttempts,
      failedMeasurements: failedMeasurements.length,
      successfulMeasurements: successfulMeasurements.length,
      measurements: measurements,
    };
  }

  /**
   * Valider la cohérence des PBO dans les tentatives
   */

  static validateFailurePBO(pboValues) {
    const uniquePBOs = Array.from(pboValues);

    if (uniquePBOs.length === 0) {
      return {
        isValid: false,
        type: "no_pbo",
        message: "Aucun PBO trouvé",
      };
    }

    if (uniquePBOs.length > 1) {
      return {
        isValid: false,
        type: "multiple_pbo",
        message: `PBO mélangés: ${uniquePBOs.join(", ")}`,
        pboList: uniquePBOs,
      };
    }

    return {
      isValid: true,
      type: "ok",
      message: `${uniquePBOs[0]}`, // Juste la valeur, pas "PBO: valeur"
      pbo: uniquePBOs[0],
    };
  }
}
